import { DownRabbitHole32 } from '@hagerty/icons';
import clsx from 'clsx';
import React from 'react';

import { ButtonGroup } from '../ButtonGroup';
import { Image, isImage } from '../Image';
import { AggregateRating } from '../Rating';
import { isVideo, Video } from '../Video';

import type { HeroProps } from './Hero.types';

export const Hero = ({
  background,
  label,
  title,
  summary,
  buttons,
  helpText,
  helpLink,
  rating,
  reviewsLink,
  moreArrowHref,
  secondaryMedia,
  secondaryHelpText,
  secondaryHelpLink,
  isCompact,
  isCompactAlt,
  isDarkText,
  isSmallTitle = isCompact || isCompactAlt,
  isRounded,
  backgroundEffect,
  backgroundPosition,
  backgroundSize,
  contentPosition,
  testId,
}: HeroProps): JSX.Element => {
  return (
    <>
      <section
        className={clsx('hds-hero', {
          'has-secondary':
            secondaryHelpText || secondaryHelpLink || secondaryMedia,
          'hds-hero_compact': isCompact || isCompactAlt,
          'hds-hero_compact_alt': isCompactAlt,
          'hds-hero_rounded': isRounded,
          'hds-hero_light': isDarkText,
          'with-rating': rating,
        })}
        data-testid={testId}
      >
        <div
          style={{
            backgroundImage: `url(${background.img.src})`,
          }}
          className={clsx('hds-hero__bg', {
            [`hds-hero__bg_pos_${backgroundPosition}`]: backgroundPosition,
            [`hds-hero__bg_size_${backgroundSize}`]: backgroundSize,
            [`hds-hero__bg_${backgroundEffect}`]: backgroundEffect,
          })}
        />
        <div className='hds-hero__container'>
          <div
            className={clsx('hds-hero__content', {
              [`hds-hero__content_${contentPosition}`]: contentPosition,
            })}
          >
            {label && <p className='hds-hero__label'>{label}</p>}
            {title && (
              <h2
                className={clsx('hds-hero__title', {
                  'hds-hero__title_md': isSmallTitle,
                })}
              >
                {title}
              </h2>
            )}
            {summary && (
              <div
                className={clsx('hds-hero__summary', {
                  'hds-hero_light': isDarkText,
                })}
              >
                {summary}
              </div>
            )}
            {buttons && (
              <ButtonGroup
                classNames='hds-hero__button-group'
                buttons={buttons}
              />
            )}
            {(helpText || helpLink) && (
              <HelpSection
                text={helpText}
                link={helpLink}
                className='hds-hero'
              />
            )}
            {rating && (
              <div className='hds-hero__rating'>
                <AggregateRating
                  rating={rating}
                  isBackgroundDark={!isDarkText}
                  isNumericVisible={true}
                  reviewsLink={reviewsLink}
                />
              </div>
            )}
          </div>
          {moreArrowHref && (
            <div className='hds-hero__more'>
              <a href={moreArrowHref} aria-label='Jump to content'>
                <DownRabbitHole32 />
              </a>
            </div>
          )}
        </div>
      </section>
      {(secondaryMedia || secondaryHelpText || secondaryHelpLink) && (
        <div className='secondary-hero'>
          {secondaryMedia && (
            <div className='secondary-hero__media'>
              {isImage(secondaryMedia) && <Image {...secondaryMedia} />}
              {isVideo(secondaryMedia) && <Video {...secondaryMedia} />}
            </div>
          )}
          {(secondaryHelpText || secondaryHelpLink) && (
            <HelpSection
              text={secondaryHelpText}
              link={secondaryHelpLink}
              className='secondary-hero'
            />
          )}
        </div>
      )}
    </>
  );
};

const HelpSection = ({
  text,
  link,
  className,
}: {
  text: HeroProps['helpText'] | HeroProps['secondaryHelpText'];
  link: HeroProps['helpLink'] | HeroProps['secondaryHelpLink'];
  className: 'hds-hero' | 'secondary-hero';
}) => {
  return (
    <div className={`${className}__help`}>
      {text && <span className={`${className}__signin`}>{text}&nbsp;</span>}
      {link && (
        <a href={link.url} className={`${className}__login`}>
          {link.text}
        </a>
      )}
    </div>
  );
};
